import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  User, LogOut, Clock, Home, Users, DollarSign,
  LayoutDashboard, FileText, Route, Send, CheckCircle, Palette, Car, Settings as SettingsIcon, Scale, Mail, MessageSquare, CalendarDays, HelpCircle, Sparkles, Crown, Ship, Handshake
} from "lucide-react";

import BookingsTab from "../components/admin/BookingsTab";
import RoutesTab from "../components/admin/RoutesTab";
import UsersTab from "../components/admin/UsersTab";
import CustomersTab from "../components/admin/CustomersTab";
import CustomerDetailModal from "../components/admin/CustomerDetailModal";
import AddBookingModal from "../components/admin/AddBookingModal";
import ModifyBookingModal from "../components/admin/ModifyBookingModal";
import RouteFormModal from "../components/admin/RouteFormModal";
import ThemeTab from "../components/admin/ThemeTab";
import DriversTab from "../components/admin/DriversTab";
import SettingsTab from "../components/admin/SettingsTab";
import LegalTab from "../components/admin/LegalTab";
import FAQTab from "../components/admin/FAQTab";
import WhyChooseUsTab from "../components/admin/WhyChooseUsTab";
import LuxuryOptionsTab from "../components/admin/LuxuryOptionsTab";
import VeniceHotelsTab from "../components/admin/VeniceHotelsTab";
import RouteCategoriesTab from "../components/admin/RouteCategoriesTab";
import VehiclesTab from "../components/admin/VehiclesTab";
import EmailTemplatesTab from "../components/admin/EmailTemplatesTab";
import PartnerInvitesTab from "../components/admin/PartnerInvitesTab";
import ReviewsTab from "../components/admin/ReviewsTab";
import AssignDriverModal from "../components/admin/AssignDriverModal";
import CalendarTab from "../components/admin/CalendarTab";
import ImportExportButtons from "../components/admin/ImportExportButtons";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === "super_admin";
  const [permissions, setPermissions] = useState(null); // null = loading, [] = none
  const hasPerm = useCallback((key) => {
    if (isSuperAdmin) return true;
    return Array.isArray(permissions) && permissions.includes(key);
  }, [isSuperAdmin, permissions]);

  const [quotes, setQuotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("bookings");

  // Filters (bookings)
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOrigin, setFilterOrigin] = useState("all");
  const [filterDestination, setFilterDestination] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterTimeFrom, setFilterTimeFrom] = useState("all");
  const [filterTimeTo, setFilterTimeTo] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [routeSearch, setRouteSearch] = useState("");
  const [stripeLiveMode, setStripeLiveMode] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/payments/config`)
      .then((res) => { if (alive) setStripeLiveMode(!!res.data?.live_mode); })
      .catch(() => { /* leave default */ });
    // Fetch effective permissions for the current admin — super_admin gets all,
    // admin gets the list stored on their user record.
    axios.get(`${API}/admin/me`)
      .then((res) => { if (alive) setPermissions(res.data?.permissions || []); })
      .catch(() => { if (alive) setPermissions([]); });
    return () => { alive = false; };
  }, []);

  // Modals
  const [showAddBooking, setShowAddBooking] = useState(false);
  const [showAddRoute, setShowAddRoute] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [modifyingQuote, setModifyingQuote] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [assigningDriver, setAssigningDriver] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const archivedParam = showArchived ? "&include_archived=true" : "";
      const statusParam = filterStatus !== "all" ? `?status=${filterStatus}` : "?status=all";
      const promises = [
        axios.get(`${API}/admin/quotes${statusParam}${archivedParam}`),
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/customers`)
      ];
      if (isSuperAdmin) {
        promises.push(axios.get(`${API}/admin/users`));
      } else {
        promises.push(Promise.resolve({ data: [] }));
      }
      if (hasPerm("routes")) {
        promises.push(axios.get(`${API}/admin/routes`));
      } else {
        promises.push(axios.get(`${API}/routes`));
      }
      const [quotesRes, statsRes, customersRes, usersRes, routesRes] = await Promise.all(promises);
      setQuotes(quotesRes.data);
      setStats(statsRes.data);
      setCustomers(customersRes.data);
      setUsers(usersRes.data);
      setRoutes(routesRes.data);
    } catch (_error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, filterStatus, showArchived, hasPerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/quotes/${quoteId}`, { status: newStatus });
      toast.success("Status updated");
      // If admin set status to confirmed and there's no driver yet, open assignment popup
      if (newStatus === "confirmed") {
        const quote = quotes.find(q => q.quote_id === quoteId);
        if (quote && !quote.driver_id) {
          setAssigningDriver({ ...quote, status: "confirmed" });
        }
      }
      fetchData();
    } catch (_error) { toast.error("Failed to update status"); }
  };

  const archiveQuote = async (quoteId) => {
    try {
      await axios.patch(`${API}/admin/quotes/${quoteId}/archive`);
      toast.success("Booking archived");
      fetchData();
    } catch (_error) { toast.error("Failed to archive booking"); }
  };

  const togglePaid = async (quoteId, paid) => {
    try {
      await axios.patch(`${API}/admin/quotes/${quoteId}/paid`, { paid });
      toast.success(paid ? "Marked as paid" : "Paid flag removed");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update paid flag"); }
  };

  const toggleRefunded = async (quoteId, refunded) => {
    try {
      await axios.patch(`${API}/admin/quotes/${quoteId}/refunded`, { refunded });
      toast.success(refunded ? "Refund issued — customer notified" : "Refund flag removed");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update refund flag"); }
  };

  const resendPaymentLink = async (q) => {
    if (!window.confirm(`Re-send the Stripe payment link to ${q.user_email}?`)) return;
    try {
      const res = await axios.post(`${API}/admin/quotes/${q.quote_id}/resend-payment-link`);
      toast.success(`Payment link sent to ${res.data.recipient}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend payment link");
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/role`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update role"); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}?\nThis CANNOT be undone. Bookings made by this user will keep the email/name as text.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.user_id}`);
      toast.success("User deleted");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete user"); }
  };

  const promoteCustomer = async (customer, newRole) => {
    if (!customer?.user_id) {
      toast.error("This customer has no account yet. They must sign in once before they can be promoted.");
      return;
    }
    const label = { admin: "Admin", super_admin: "Super Admin", driver: "Driver" }[newRole];
    const extra = newRole === "driver" ? "\nA driver record will be created and login credentials emailed to them." : "";
    if (!window.confirm(`Promote ${customer.email} to ${label}?${extra}`)) return;
    try {
      await axios.post(`${API}/admin/customers/${customer.user_id}/promote`, { role: newRole });
      toast.success(`Promoted to ${label}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to promote"); }
  };

  const deleteBooking = async (q) => {
    if (!window.confirm(`Permanently delete booking ${q.quote_id}?\nThis CANNOT be undone (status history, reviews and payment records will also be removed).`)) return;
    try {
      await axios.delete(`${API}/admin/quotes/${q.quote_id}`);
      toast.success("Booking deleted");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete booking"); }
  };

  const deleteDriver = async (d) => {
    if (!window.confirm(`Permanently delete driver ${d.name} ${d.surname}?\nThis CANNOT be undone. Any bookings assigned to this driver will be detached but kept.`)) return;
    try {
      await axios.delete(`${API}/admin/drivers/${d.driver_id}`);
      toast.success("Driver deleted");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete driver"); }
  };

  const deleteRoute = async (routeId) => {
    if (!window.confirm("Delete this route? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/routes/${routeId}`);
      toast.success("Route deleted");
      fetchData();
    } catch (_error) { toast.error("Failed to delete route"); }
  };

  const toggleRouteDisabled = async (route) => {
    const next = !route.disabled;
    try {
      await axios.put(`${API}/admin/routes/${route.route_id}`, { disabled: next });
      toast.success(next ? "Route disabled — hidden from public lists" : "Route re-enabled");
      fetchData();
    } catch (e) { toast.error("Failed to update route"); }
  };

  const cloneRoute = async (route) => {
    try {
      const res = await axios.post(`${API}/admin/routes/${route.route_id}/clone`);
      toast.success(`Cloned — new slug: ${res.data.slug} (Disabled)`);
      fetchData();
    } catch (e) { toast.error("Failed to clone route"); }
  };

  const exportBookings = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("status", filterStatus);
      const response = await axios.get(`${API}/admin/bookings/export?${params}`, { responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `taxinearme_bookings_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Bookings exported to Excel");
    } catch (_error) { toast.error("Failed to export"); }
  };

  const uniqueOrigins = useMemo(() => [...new Set(quotes.map(q => q.origin).filter(Boolean))].sort(), [quotes]);
  const uniqueDestinations = useMemo(() => [...new Set(quotes.map(q => q.destination).filter(Boolean))].sort(), [quotes]);

  const filteredQuotes = useMemo(() => {
    let result = quotes;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r =>
        r.user_name?.toLowerCase().includes(q) ||
        r.user_email?.toLowerCase().includes(q) ||
        r.quote_id?.toLowerCase().includes(q) ||
        r.user_phone?.includes(q)
      );
    }
    if (filterOrigin !== "all") result = result.filter(r => r.origin === filterOrigin);
    if (filterDestination !== "all") result = result.filter(r => r.destination === filterDestination);
    if (filterDateFrom) result = result.filter(r => r.pickup_date >= filterDateFrom);
    if (filterDateTo) result = result.filter(r => r.pickup_date <= filterDateTo);
    if (filterTimeFrom !== "all") result = result.filter(r => r.pickup_time >= filterTimeFrom);
    if (filterTimeTo !== "all") result = result.filter(r => r.pickup_time <= filterTimeTo);
    return result;
  }, [quotes, searchQuery, filterOrigin, filterDestination, filterDateFrom, filterDateTo, filterTimeFrom, filterTimeTo]);

  const filteredRoutes = useMemo(() => {
    if (!routeSearch) return routes;
    const q = routeSearch.toLowerCase();
    return routes.filter(r =>
      r.origin?.toLowerCase().includes(q) || r.destination?.toLowerCase().includes(q) ||
      r.category?.toLowerCase().includes(q) || r.slug?.toLowerCase().includes(q)
    );
  }, [routes, routeSearch]);

  const clearFilters = () => {
    setSearchQuery(""); setFilterOrigin("all"); setFilterDestination("all");
    setFilterDateFrom(""); setFilterDateTo(""); setFilterTimeFrom("all"); setFilterTimeTo("all");
  };

  const hasActiveFilters = searchQuery || filterOrigin !== "all" || filterDestination !== "all" || filterDateFrom || filterDateTo || filterTimeFrom !== "all" || filterTimeTo !== "all";

  const tabs = [
    { id: "bookings", label: "Bookings", icon: FileText, perm: "bookings" },
    { id: "calendar", label: "Calendar", icon: CalendarDays, perm: "calendar" },
    { id: "customers", label: "Customers", icon: Users, perm: "customers" },
    { id: "drivers", label: "Drivers", icon: Car, perm: "drivers" },
    { id: "reviews", label: "Reviews", icon: MessageSquare, perm: "reviews" },
    { id: "venice_hotels", label: "Venice Hotels", icon: Ship, perm: "venice_hotels" },
    { id: "route_categories", label: "Category Pages", icon: Route, perm: "routes" },
    { id: "routes", label: "Routes", icon: Route, perm: "routes", superOnly: true },
    { id: "users", label: "Staff", icon: Users, superOnly: true },  // super_admin only — permission editor
    { id: "settings", label: "Settings", icon: SettingsIcon, perm: "settings" },
    { id: "emails", label: "Emails", icon: Mail, perm: "emails" },
    { id: "partner_invites", label: "Partner Invites", icon: Handshake, superOnly: true },
    { id: "theme", label: "Theme", icon: Palette, perm: "theme" },
    { id: "legal", label: "Legal", icon: Scale, perm: "legal" },
    { id: "faq", label: "FAQ", icon: HelpCircle, perm: "faqs" },
    { id: "why_us", label: "Why Us", icon: Sparkles, perm: "why_us" },
    { id: "luxury", label: "Premium", icon: Crown, perm: "luxury_options" },
    { id: "fleet", label: "Fleet", icon: Car, perm: "vehicles" },
  ].filter(t => {
    if (t.superOnly) return isSuperAdmin;
    if (!t.perm) return true;
    return hasPerm(t.perm);
  });

  // If the current activeTab is not in the filtered set (e.g. admin lost a permission),
  // switch to the first available tab.
  useEffect(() => {
    if (permissions === null) return;  // still loading
    if (tabs.length && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [permissions, activeTab, tabs]);

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <aside className="admin-sidebar w-64 min-h-screen hidden lg:flex flex-col" data-testid="admin-sidebar">
        <div className="p-6">
          <Link to="/" className="text-2xl font-bold logo-text">TN</Link>
          <p className="text-white/40 text-xs mt-1">Admin Panel</p>
        </div>
        <nav className="mt-2 flex-1">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`admin-nav-item w-full ${activeTab === tab.id ? "active" : ""}`}
              data-testid={`nav-${tab.id}`}>
              <tab.icon className="w-5 h-5" /><span>{tab.label}</span>
            </button>
          ))}
          <div className="border-t border-white/5 mt-4 pt-4">
            <Link to="/dashboard" className="admin-nav-item"><LayoutDashboard className="w-5 h-5" /><span>User Dashboard</span></Link>
            <Link to="/" className="admin-nav-item"><Home className="w-5 h-5" /><span>Back to Site</span></Link>
          </div>
        </nav>
        <div className="p-6 border-t border-white/5">
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" /> :
              <div className="w-10 h-10 rounded-full bg-[#C5A059]/20 flex items-center justify-center"><User className="w-5 h-5 text-[#C5A059]" /></div>}
            <div>
              <p className="text-white text-sm font-medium truncate max-w-[140px]">{user?.name}</p>
              <p className={`text-xs uppercase ${isSuperAdmin ? "text-purple-400" : "text-[#C5A059]"}`}>{isSuperAdmin ? "Super Admin" : "Admin"}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={() => { logout(); navigate("/"); }} className="w-full justify-start text-white/60 hover:text-red-400" data-testid="admin-logout">
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
          {stripeLiveMode && (
            <div className="mt-3 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-sm flex items-center gap-2" data-testid="stripe-live-badge">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-red-300 text-[10px] uppercase tracking-widest font-semibold">Stripe Live</span>
              <span className="text-red-400/60 text-[10px]">- real cards</span>
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <header className="lg:hidden bg-[#0A0A0A] border-b border-white/5 p-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-xl font-bold logo-text">TN Admin</Link>
            <Button variant="ghost" onClick={() => { logout(); navigate("/"); }} className="text-white/60"><LogOut className="w-5 h-5" /></Button>
          </div>
          <div className="flex gap-2 mt-4 overflow-x-auto">
            {tabs.map(tab => (
              <Button key={tab.id} variant={activeTab === tab.id ? "default" : "ghost"}
                onClick={() => setActiveTab(tab.id)} size="sm"
                className={activeTab === tab.id ? "bg-[#C5A059] text-black" : "text-white/60"}>{tab.label}</Button>
            ))}
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-7 gap-4 mb-8" data-testid="stats-cards">
              <StatCard icon={FileText} label="Active" value={stats.total_quotes} />
              <StatCard icon={Clock} label="Pending" value={stats.pending_quotes} accent />
              <StatCard icon={DollarSign} label="Awaiting Pay" value={stats.awaiting_payment_quotes || 0} color="text-orange-400" />
              <StatCard icon={CheckCircle} label="Confirmed" value={stats.confirmed_quotes} color="text-emerald-400" />
              <StatCard icon={Send} label="Proposals" value={stats.proposal_sent} color="text-violet-400" />
              <StatCard icon={Users} label="Users" value={stats.total_users} />
              <StatCard icon={DollarSign} label="Revenue" value={`\u20AC${stats.total_revenue}`} accent />
            </div>
          )}

          {loading ? <div className="flex justify-center py-20"><div className="spinner" /></div> : (
            <>
              {activeTab === "bookings" && (
                <div className="space-y-4">
                  {isSuperAdmin && <ImportExportButtons entity="bookings" onImported={fetchData} />}
                  <BookingsTab
                  quotes={filteredQuotes} filterStatus={filterStatus} setFilterStatus={setFilterStatus}
                  searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                  filterOrigin={filterOrigin} setFilterOrigin={setFilterOrigin}
                  filterDestination={filterDestination} setFilterDestination={setFilterDestination}
                  filterDateFrom={filterDateFrom} setFilterDateFrom={setFilterDateFrom}
                  filterDateTo={filterDateTo} setFilterDateTo={setFilterDateTo}
                  filterTimeFrom={filterTimeFrom} setFilterTimeFrom={setFilterTimeFrom}
                  filterTimeTo={filterTimeTo} setFilterTimeTo={setFilterTimeTo}
                  showArchived={showArchived} setShowArchived={setShowArchived}
                  uniqueOrigins={uniqueOrigins} uniqueDestinations={uniqueDestinations}
                  updateQuoteStatus={updateQuoteStatus} archiveQuote={archiveQuote}
                  exportBookings={exportBookings} clearFilters={clearFilters}
                  hasActiveFilters={hasActiveFilters}
                  onAddBooking={() => setShowAddBooking(true)}
                  onModifyQuote={setModifyingQuote}
                  onAssignDriver={setAssigningDriver}
                  onTogglePaid={togglePaid}
                  onToggleRefunded={toggleRefunded}
                  onResendPaymentLink={resendPaymentLink}
                  onDeleteBooking={isSuperAdmin ? deleteBooking : null}
                />
                </div>
              )}
              {activeTab === "calendar" && (
                <CalendarTab scope="admin" />
              )}
              {activeTab === "customers" && (
                <div className="space-y-4">
                  {isSuperAdmin && <ImportExportButtons entity="customers" onImported={fetchData} />}
                  <CustomersTab
                    customers={customers}
                    onSelectCustomer={setSelectedCustomer}
                    onPromoteCustomer={isSuperAdmin ? promoteCustomer : null}
                    onDeleteCustomer={isSuperAdmin ? deleteUser : null}
                    isSuperAdmin={isSuperAdmin}
                  />
                </div>
              )}
              {activeTab === "drivers" && (
                <div className="space-y-4">
                  {isSuperAdmin && <ImportExportButtons entity="drivers" onImported={fetchData} />}
                  <DriversTab canEdit={isSuperAdmin} onDeleteDriver={isSuperAdmin ? deleteDriver : null} />
                </div>
              )}
              {activeTab === "settings" && isSuperAdmin && (
                <SettingsTab />
              )}
              {activeTab === "routes" && isSuperAdmin && (
                <div className="space-y-4">
                  <ImportExportButtons entity="routes" onImported={fetchData} />
                  <RoutesTab routes={filteredRoutes} routeSearch={routeSearch} setRouteSearch={setRouteSearch}
                    deleteRoute={deleteRoute} onAddRoute={() => setShowAddRoute(true)} onEditRoute={setEditingRoute}
                    onToggleDisable={toggleRouteDisabled} onCloneRoute={cloneRoute} />
                </div>
              )}
              {activeTab === "users" && isSuperAdmin && (
                <UsersTab users={users} currentUserId={user?.user_id} updateUserRole={updateUserRole} deleteUser={deleteUser} onRefresh={fetchData} />
              )}
              {activeTab === "theme" && isSuperAdmin && (
                <ThemeTab />
              )}
              {activeTab === "legal" && isSuperAdmin && (
                <LegalTab />
              )}
              {activeTab === "faq" && isSuperAdmin && (
                <FAQTab />
              )}
              {activeTab === "why_us" && isSuperAdmin && (
                <WhyChooseUsTab />
              )}
              {activeTab === "luxury" && isSuperAdmin && (
                <LuxuryOptionsTab />
              )}
              {activeTab === "fleet" && (
                <VehiclesTab />
              )}
              {activeTab === "emails" && isSuperAdmin && (
                <EmailTemplatesTab />
              )}
              {activeTab === "partner_invites" && isSuperAdmin && (
                <PartnerInvitesTab />
              )}
              {activeTab === "reviews" && (
                <ReviewsTab />
              )}
              {activeTab === "venice_hotels" && (
                <VeniceHotelsTab />
              )}
              {activeTab === "route_categories" && (
                <RouteCategoriesTab />
              )}
            </>
          )}
        </div>
      </main>

      {showAddBooking && <AddBookingModal routes={routes} onClose={() => setShowAddBooking(false)} onSuccess={() => { setShowAddBooking(false); fetchData(); }} />}
      {showAddRoute && <RouteFormModal onClose={() => setShowAddRoute(false)} onSuccess={() => { setShowAddRoute(false); fetchData(); }} />}
      {editingRoute && <RouteFormModal route={editingRoute} onClose={() => setEditingRoute(null)} onSuccess={() => { setEditingRoute(null); fetchData(); }} />}
      {modifyingQuote && <ModifyBookingModal quote={modifyingQuote} routes={routes} onClose={() => setModifyingQuote(null)} onSuccess={() => { setModifyingQuote(null); fetchData(); }} />}
      {selectedCustomer && (
        <CustomerDetailModal
          customerEmail={selectedCustomer}
          routes={routes}
          onClose={() => setSelectedCustomer(null)}
          onDataChange={fetchData}
        />
      )}
      {assigningDriver && (
        <AssignDriverModal
          quote={assigningDriver}
          onClose={() => setAssigningDriver(null)}
          onAssigned={() => { setAssigningDriver(null); fetchData(); }}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent = false, color }) {
  return (
    <div className="bg-[#121212] border border-white/5 rounded-sm p-4">
      <Icon className={`w-5 h-5 mb-2 ${color || (accent ? "text-[#C5A059]" : "text-white/30")}`} />
      <p className={`text-2xl font-bold font-mono ${color || (accent ? "text-[#C5A059]" : "text-white")}`}>{value}</p>
      <p className="text-white/40 text-xs mt-1">{label}</p>
    </div>
  );
}
